import { useState } from 'react';
import DatePicker from 'components/base/DatePicker';
import Button from 'components/base/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import BaseMultiDropdown from 'pages/common/BaseMultiDropdown';
import type { BaseMultiDropDownItem } from 'pages/common/BaseMultiDropdown';
import type IOrderViewCollectionFieldFilterParams from 'models/params/IOrderViewCollectionFieldFilterParams';

interface OrdersFilterProps {
  ordersFilterObject: IOrderViewCollectionFieldFilterParams[];
  setOrdersFilterObject: (
    filters: IOrderViewCollectionFieldFilterParams[]
  ) => object;
}

export type TOrdersFilterObject = Record<
  string,
  string | string[] | Date[] | BaseMultiDropDownItem[] | undefined
>;
const filterFields = [
  {
    key: 'orderName',
    label: 'NAME',
    placeholder: 'Search by Order Name...',
    type: 'text',
    apiFieldName: 'Name' as IOrderViewCollectionFieldFilterParams['fieldName'],
    comparison: '_txwt' as IOrderViewCollectionFieldFilterParams['comparison']
  },
  {
    key: 'externalId',
    label: 'EXTERNAL ID',
    placeholder: 'Search by External Id...',
    type: 'text',
    apiFieldName:
      'ExternalID' as IOrderViewCollectionFieldFilterParams['fieldName'],
    comparison: '_txwt' as IOrderViewCollectionFieldFilterParams['comparison']
  },
  {
    key: 'date',
    label: 'DATE RANGE',
    type: 'date-range',
    apiFieldNames: ['Created', 'Created'] as [
      IOrderViewCollectionFieldFilterParams['fieldName'],
      IOrderViewCollectionFieldFilterParams['fieldName']
    ], // Соответствующие поля API
    comparisons: ['_gteq', '_lteq'] as [
      IOrderViewCollectionFieldFilterParams['comparison'],
      IOrderViewCollectionFieldFilterParams['comparison']
    ]
  },
  {
    key: 'paymentStatus',
    label: 'PAYMENT STATUS',
    type: 'multi-select',
    apiFieldName:
      'PaymentStatus' as IOrderViewCollectionFieldFilterParams['fieldName'],
    comparison: '_eq' as IOrderViewCollectionFieldFilterParams['comparison'],
    options: [
      { value: 'NONE', label: 'NONE' },
      { value: 'WAITING', label: 'WAITING' },
      { value: 'PAID', label: 'PAID' },
      { value: 'POST_PAID', label: 'POST_PAID' },
      { value: 'CHARGEDBACK', label: 'CHARGEDBACK' }
    ]
  },
  {
    key: 'orderStatus',
    label: 'ORDER STATUS',
    type: 'multi-select',
    apiFieldName:
      'OrderStatusID' as IOrderViewCollectionFieldFilterParams['fieldName'],
    comparison: '_eq' as IOrderViewCollectionFieldFilterParams['comparison'],
    options: [
      { value: '2', label: 'Awaiting Payment' },
      { value: '3', label: 'Info Request' },
      { value: '4', label: 'No Source File' },
      { value: '5', label: 'Wrong Address' },
      { value: '6', label: 'Printing' },
      { value: '7', label: 'Ready For Shipping' },
      { value: '8', label: 'Shipped' },
      { value: '9', label: 'Canceled' },
      { value: '10', label: 'Missing Data' }
    ]
  },
  {
    key: 'store',
    label: 'Store',
    placeholder: 'Search store',
    type: 'text',
    apiFieldName:
      'StoreTitle' as IOrderViewCollectionFieldFilterParams['fieldName'],
    comparison: '_eq' as IOrderViewCollectionFieldFilterParams['comparison']
  },
  {
    key: 'trackingId',
    label: 'Tracking ID',
    placeholder: 'Search by Tracking ID',
    type: 'text',
    apiFieldName:
      'TrackingNumber' as IOrderViewCollectionFieldFilterParams['fieldName'],
    comparison: '_txwt' as IOrderViewCollectionFieldFilterParams['comparison']
  }
];

const OrdersFilter = ({ setOrdersFilterObject }: OrdersFilterProps) => {
  const [filters, setFilters] = useState<TOrdersFilterObject>({});
  const [isFiltered, setIsFiltered] = useState(false);

  const handleChange = (
    key: string,
    value: string | string[] | Date[] | BaseMultiDropDownItem[] | undefined
  ) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleItemChange = (
    itemId: string | number,
    selected: BaseMultiDropDownItem[]
  ) => {
    handleChange(itemId as string, selected);

    const newSelected = { [itemId]: selected };
    const hasItems = Object.values(newSelected).some(
      arr => (arr as BaseMultiDropDownItem[]).length > 0
    );
    const hasSearch = Object.values(filters).some(
      val => typeof val === 'string' && val.length > 0
    );
    setIsFiltered(hasItems || hasSearch);
    return newSelected;
  };

  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  const buildFieldFilters = (filters: TOrdersFilterObject) => {
    const fieldFilters: IOrderViewCollectionFieldFilterParams[] = [];
    console.log('filterFields перед forEach:', filterFields);
    filterFields.forEach(field => {
      const value = filters[field.key];

      if (
        value === undefined ||
        value === null ||
        (Array.isArray(value) && value.length === 0)
      ) {
        return;
      }

      if (field.type === 'text') {
        if (
          field.apiFieldName &&
          field.comparison &&
          typeof value === 'string' &&
          value.length > 0
        ) {
          fieldFilters.push({
            fieldName: field.apiFieldName,
            comparison: field.comparison,
            value: value
          });
        }
      } else if (
        field.type === 'date-range' &&
        Array.isArray(value) &&
        value.length === 2
      ) {
        if (
          field.apiFieldNames &&
          field.comparisons &&
          field.apiFieldNames.length === 2 &&
          field.comparisons.length === 2
        ) {
          const [startDate, endDate] = value as [Date, Date];
          fieldFilters.push({
            fieldName: field.apiFieldNames[0],
            comparison: field.comparisons[0],
            value: formatDate(startDate)
          });
          fieldFilters.push({
            fieldName: field.apiFieldNames[1],
            comparison: field.comparisons[1],
            value: formatDate(endDate)
          });
        }
      } else if (field.type === 'select') {
        if (
          field.apiFieldName &&
          field.comparison &&
          typeof value === 'string' &&
          value.length > 0
        ) {
          fieldFilters.push({
            fieldName: field.apiFieldName,
            comparison: field.comparison,
            value: value
          });
        }
      } else if (field.type === 'multi-select') {
        if (
          field.apiFieldName &&
          field.comparison &&
          Array.isArray(value) &&
          value.length > 0
        ) {
          const selectedValues = (value as BaseMultiDropDownItem[])
            .map(item => item.value)
            .filter(v => v !== undefined) as string[];
          if (selectedValues.length > 0) {
            console.log('Значения для API (multi-select):', {
              fieldName: field.apiFieldName,
              comparison: field.comparison,
              value: selectedValues.join(',')
            });
            fieldFilters.push({
              fieldName: field.apiFieldName,
              comparison: field.comparison,
              value: selectedValues.join(',')
            });
          }
        }
      }
    });
    setOrdersFilterObject(fieldFilters);
  };

  return (
    <div className="d-flex align-items-end gap-3 flex-wrap mb-3">
      {filterFields.map(field => (
        <div key={field.key} style={{ minWidth: 180 }}>
          <label className="form-label mb-1" style={{ fontSize: 12 }}>
            {field.label}
          </label>
          {field.type === 'text' && (
            <input
              className="form-control"
              placeholder={field.placeholder}
              value={
                typeof filters[field.key] === 'string' ||
                typeof filters[field.key] === 'number'
                  ? String(filters[field.key])
                  : ''
              }
              onChange={e => handleChange(field.key, e.target.value)}
            />
          )}
          {field.type === 'date-range' && (
            <DatePicker
              value={filters[field.key] as Date[] | undefined}
              onChange={date => handleChange(field.key, date)}
              options={{ mode: 'range' }}
              placeholder="Select date range"
            />
          )}
          {field.type === 'multi-select' && (
            <BaseMultiDropdown
              key={field.key}
              isFiltered={isFiltered}
              title={field.label}
              items={(field.options ?? []).map(opt => ({
                ID: opt.value,
                Title: opt.label,
                value: opt.value,
                label: opt.label
              }))}
              onItemChange={(Id, selected) =>
                handleItemChange((Id = field.key), selected)
              }
              itemId={field.key}
            />
          )}
        </div>
      ))}
      <Button
        variant="outline-secondary"
        onClick={() => buildFieldFilters(filters)}
        className="d-flex align-items-center justify-content-center"
      >
        <FontAwesomeIcon icon={faMagnifyingGlass} size="lg" />
      </Button>
    </div>
  );
};

export default OrdersFilter;
